import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  animate,
  query,
  stagger,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { AiFeature } from './ai-solutions.component';

/** Expo-style out curve used across the whole accordion timeline. */
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EASE_IN = 'cubic-bezier(0.4, 0, 1, 1)';

@Component({
  selector: 'app-ai-feature-card',
  templateUrl: './ai-feature-card.component.html',
  styleUrls: ['./ai-feature-card.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    /* Width morph. Both the outgoing and incoming card run this at the same
       time, so the active state reads as one continuous transfer. */
    /* 7 cards: 6 collapsed + 1 expanded must total 100% of the rail.
       6 x 10.4 + 37.6 = 100. */
    trigger('accordion', [
      state('collapsed', style({ width: '10.4%' })),
      state('expanded', style({ width: '37.6%' })),
      transition('collapsed <=> expanded', animate(`650ms ${EASE_OUT}`)),
    ]),
    /* Detail block: staggered in behind the width morph, swept out fast so the
       collapsing card never competes with the one taking over. */
    trigger('detail', [
      transition(':enter', [
        query(
          '.card__rule, .card__text, .card__dots',
          [
            style({ opacity: 0, transform: 'translateX(14px)' }),
            stagger(60, [
              animate(
                `420ms 120ms ${EASE_OUT}`,
                style({ opacity: 1, transform: 'none' })
              ),
            ]),
          ],
          { optional: true }
        ),
      ]),
      transition(':leave', [
        /* paragraph and decorations clear first; the heading is left to
           settle last as the card keeps shrinking underneath it */
        query(
          '.card__text, .card__dots, .card__rule',
          [
            stagger(35, [
              animate(
                `150ms ${EASE_IN}`,
                style({ opacity: 0, transform: 'translateX(10px)' })
              ),
            ]),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class AiFeatureCardComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) feature!: AiFeature;
  @Input() expanded = false;
  @Output() activate = new EventEmitter<void>();

  /** Drives the click ripple; cleared on animationend, never on a timer. */
  readonly ripple = signal(false);

  @HostBinding('@accordion') get accordionState(): 'collapsed' | 'expanded' {
    return this.expanded ? 'expanded' : 'collapsed';
  }

  @HostBinding('class.is-expanded') get expandedClass(): boolean {
    return this.expanded;
  }

  @HostBinding('attr.role') readonly role = 'tab';
  @HostBinding('attr.tabindex') readonly tabindex = '0';

  @HostBinding('attr.aria-selected') get ariaSelected(): string {
    return String(this.expanded);
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (this.expanded) return;
    const el = this.host.nativeElement;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${event.clientX - r.left}px`);
    el.style.setProperty('--my', `${event.clientY - r.top}px`);
    // restart the ripple cleanly on repeat clicks
    this.ripple.set(false);
    requestAnimationFrame(() => this.ripple.set(true));
  }

  @HostListener('click')
  onClick(): void {
    if (!this.expanded) this.activate.emit();
  }

  @HostListener('keydown.enter', ['$event'])
  @HostListener('keydown.space', ['$event'])
  onKey(event: Event): void {
    event.preventDefault();
    if (!this.expanded) this.activate.emit();
  }

  private cleanups: Array<() => void> = [];

  constructor(
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Pointer tilt, desktop only, and only while this card is expanded.
   * rAF-throttled outside the zone so it never triggers change detection.
   */
  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const el = this.host.nativeElement;
    if (!fine || reduced) return;

    const MAX = 5; // degrees
    let queued = false;
    let rx = 0;
    let ry = 0;

    const apply = () => {
      queued = false;
      el.style.setProperty('--rx', `${rx.toFixed(2)}deg`);
      el.style.setProperty('--ry', `${ry.toFixed(2)}deg`);
    };

    const onMove = (e: MouseEvent) => {
      if (!this.expanded) return;
      const r = el.getBoundingClientRect();
      rx = -((e.clientY - r.top) / r.height - 0.5) * MAX * 2;
      ry = ((e.clientX - r.left) / r.width - 0.5) * MAX * 2;
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    const onLeave = () => {
      rx = 0;
      ry = 0;
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    this.zone.runOutsideAngular(() => {
      el.addEventListener('mousemove', onMove, { passive: true });
      el.addEventListener('mouseleave', onLeave, { passive: true });
    });

    this.cleanups.push(() => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    });
  }

  ngOnDestroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
  }
}
